/**
 * Dictation for search boxes, via the browser's own speech recognition.
 *
 * The Web Speech API rather than recording audio and posting it to our Whisper
 * deployment: this is a short search phrase, the browser transcribes it live
 * with interim results as you speak, and it costs nothing per use. Whisper
 * would add a round trip and a bill for every "two bed near Dubai Mall".
 *
 * The trade-off worth knowing: in Chrome this ships the audio to Google's
 * speech service. That is fine for a search phrase typed into a public-facing
 * search box and would NOT be fine for anything confidential — which is why
 * this hook exists for search only and is not offered on note or client
 * fields.
 *
 * Support is genuinely partial (Chrome and Edge yes, Firefox no), so
 * `supported` is exported and the caller is expected to hide the control
 * rather than show a button that does nothing.
 */
import { useCallback, useEffect, useRef, useState } from "react";

interface SpeechRecognitionAlternativeLike {
  transcript: string;
}
interface SpeechRecognitionResultLike {
  0: SpeechRecognitionAlternativeLike;
  isFinal: boolean;
  length: number;
}
interface SpeechRecognitionEventLike {
  resultIndex: number;
  results: {
    length: number;
    [index: number]: SpeechRecognitionResultLike;
  };
}
interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((e: SpeechRecognitionEventLike) => void) | null;
  onerror: ((e: { error?: string }) => void) | null;
  onend: (() => void) | null;
}

type RecognitionCtor = new () => SpeechRecognitionLike;

function getCtor(): RecognitionCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    SpeechRecognition?: RecognitionCtor;
    webkitSpeechRecognition?: RecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/**
 * Dictation languages offered.
 *
 * Chosen for who actually buys Dubai property: Arabic and English are the
 * local pair, then the largest investor nationalities — Indian, Russian,
 * Chinese and European. The recogniser needs to be told which language to
 * expect; it does not auto-detect, and left on en-GB it transcribes Arabic as
 * English-sounding nonsense rather than failing, which is the worst outcome.
 *
 * Whatever comes back is passed to the search, which translates it — so a
 * Russian phrase produces English filters.
 */
export const SPEECH_LANGUAGES: { code: string; label: string }[] = [
  { code: "en-GB", label: "English" },
  { code: "ar-AE", label: "العربية" },
  { code: "hi-IN", label: "हिन्दी" },
  { code: "ru-RU", label: "Русский" },
  { code: "zh-CN", label: "中文" },
  { code: "fr-FR", label: "Français" },
  { code: "de-DE", label: "Deutsch" },
  { code: "es-ES", label: "Español" },
  { code: "vi-VN", label: "Tiếng Việt" },
  { code: "ur-PK", label: "اردو" },
  { code: "fa-IR", label: "فارسی" },
];

export interface SpeechInput {
  supported: boolean;
  listening: boolean;
  /** Live text, including the not-yet-final part, for display while speaking. */
  interim: string;
  error: string | null;
  start: () => void;
  stop: () => void;
}

export function useSpeechInput(
  onFinal: (text: string) => void,
  lang = "en-GB",
): SpeechInput {
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<SpeechRecognitionLike | null>(null);
  // Held in a ref so restarting recognition never calls a stale closure with
  // an out-of-date setter.
  const finalRef = useRef(onFinal);
  finalRef.current = onFinal;

  const supported = getCtor() !== null;

  const stop = useCallback(() => {
    ref.current?.stop();
    setListening(false);
  }, []);

  const start = useCallback(() => {
    const Ctor = getCtor();
    if (!Ctor) return;
    setError(null);
    setInterim("");

    const rec = new Ctor();
    rec.lang = lang;
    // Single utterance: a search phrase ends when the speaker stops, and
    // continuous mode would leave the microphone open until told otherwise.
    rec.continuous = false;
    rec.interimResults = true;
    rec.maxAlternatives = 1;

    rec.onresult = (e) => {
      let finalText = "";
      let pending = "";
      for (let i = e.resultIndex; i < e.results.length; i += 1) {
        const result = e.results[i];
        if (result.isFinal) finalText += result[0].transcript;
        else pending += result[0].transcript;
      }
      setInterim(pending);
      if (finalText.trim()) {
        finalRef.current(finalText.trim());
        setInterim("");
      }
    };
    rec.onerror = (e) => {
      // "no-speech" and "aborted" are ordinary outcomes of pressing the
      // button and saying nothing; only surface the ones a user can act on.
      const code = e?.error ?? "";
      if (code === "not-allowed" || code === "service-not-allowed") {
        setError("Microphone access was blocked. Allow it in the browser and try again.");
      } else if (code === "audio-capture") {
        setError("No microphone was found.");
      } else if (code && code !== "no-speech" && code !== "aborted") {
        setError("Dictation stopped unexpectedly. Try again.");
      }
      setListening(false);
    };
    rec.onend = () => {
      setListening(false);
      setInterim("");
    };

    ref.current = rec;
    try {
      rec.start();
      setListening(true);
    } catch {
      // start() throws if called while already running — harmless.
      setListening(false);
    }
  }, [lang]);

  // Release the microphone if the component unmounts mid-utterance.
  useEffect(() => () => ref.current?.abort(), []);

  return { supported, listening, interim, error, start, stop };
}
